const PLAYER_SEASON_PREFIX = 'playerStats:seasonEntries:';
const RECENT_SUBMISSIONS_PREFIX = 'playerStats:recentSubmissions:';
const pendingPlayerSeasonLoads = new Map();

const hasSessionStorage = () => {
  try {
    return typeof window !== 'undefined' && !!window.sessionStorage;
  } catch (_) {
    return false;
  }
};

const readJson = (key) => {
  if (!hasSessionStorage()) return null;
  try {
    const raw = window.sessionStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch (_) {
    return null;
  }
};

const writeJson = (key, value) => {
  if (!hasSessionStorage()) return;
  try {
    window.sessionStorage.setItem(key, JSON.stringify(value));
  } catch (_) {
    // Ignore storage quota and serialization failures.
  }
};

const removeKey = (key) => {
  if (!hasSessionStorage()) return;
  try {
    window.sessionStorage.removeItem(key);
  } catch (_) {
    // Ignore storage removal failures.
  }
};

const readTtlJson = (key, ttlMs) => {
  const cached = readJson(key);
  if (!cached) return null;
  if (!ttlMs) return cached;
  if (
    typeof cached === 'object' &&
    !Array.isArray(cached) &&
    typeof cached.savedAt === 'number' &&
    Object.prototype.hasOwnProperty.call(cached, 'data')
  ) {
    if (Date.now() - cached.savedAt <= ttlMs) {
      return cached.data;
    }
    removeKey(key);
    return null;
  }
  removeKey(key);
  return null;
};

const writeTtlJson = (key, value) => {
  writeJson(key, {
    savedAt: Date.now(),
    data: value,
  });
};

export const buildPlayerSeasonCacheKey = (teamId, seasonId) =>
  `${PLAYER_SEASON_PREFIX}${teamId || ''}:${seasonId || ''}`;

export const buildRecentSubmissionsCacheKey = (teamId, seasonId) =>
  `${RECENT_SUBMISSIONS_PREFIX}${teamId || ''}:${seasonId || ''}`;

export const getCachedPlayerSeasonEntries = (teamId, seasonId, ttlMs = null) =>
  readTtlJson(buildPlayerSeasonCacheKey(teamId, seasonId), ttlMs);

export const setCachedPlayerSeasonEntries = (teamId, seasonId, rows) => {
  writeTtlJson(buildPlayerSeasonCacheKey(teamId, seasonId), rows || []);
};

export const loadPlayerSeasonEntriesOnce = (teamId, seasonId, loader) => {
  const cached = getCachedPlayerSeasonEntries(teamId, seasonId);
  if (Array.isArray(cached)) return Promise.resolve(cached);
  const key = buildPlayerSeasonCacheKey(teamId, seasonId);
  if (pendingPlayerSeasonLoads.has(key)) return pendingPlayerSeasonLoads.get(key);
  const pending = Promise.resolve().then(loader).then((rows) => {
    setCachedPlayerSeasonEntries(teamId, seasonId, rows);
    return rows;
  }).finally(() => pendingPlayerSeasonLoads.delete(key));
  pendingPlayerSeasonLoads.set(key, pending);
  return pending;
};

export const getCachedRecentSubmissions = (teamId, seasonId, ttlMs = null) =>
  readTtlJson(buildRecentSubmissionsCacheKey(teamId, seasonId), ttlMs);

export const setCachedRecentSubmissions = (teamId, seasonId, payload) => {
  writeTtlJson(buildRecentSubmissionsCacheKey(teamId, seasonId), payload);
};

export const clearPlayerStatsSeasonCache = (teamId, seasonId) => {
  removeKey(buildPlayerSeasonCacheKey(teamId, seasonId));
  removeKey(buildRecentSubmissionsCacheKey(teamId, seasonId));
};

export const clearAllPlayerStatsSeasonCaches = (teamId) => {
  if (!hasSessionStorage() || !teamId) return;
  try {
    const keysToRemove = [];
    for (let i = 0; i < window.sessionStorage.length; i += 1) {
      const key = window.sessionStorage.key(i);
      if (!key) continue;
      if (
        key.startsWith(`${PLAYER_SEASON_PREFIX}${teamId}:`) ||
        key.startsWith(`${RECENT_SUBMISSIONS_PREFIX}${teamId}:`)
      ) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(removeKey);
  } catch (_) {
    // Ignore storage iteration failures.
  }
};
